import React, { useState, useEffect, useMemo, useRef } from "react";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import Chip from "@mui/material/Chip";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import ReplayIcon from "@mui/icons-material/Replay";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { Divider, TextField } from "@mui/material";
import { MenuItem } from "@mui/material";
import { Select } from "@mui/material";
import { Grid } from "@mui/material";
import Typography from "@mui/material/Typography";
import ButtonGroup from "@mui/material/ButtonGroup";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
    API_BASE_URL,
  WHATSAPP_MESSAGE_LOG_ENDPOINT,
} from "../config/urls";
import Menu from "@mui/material/Menu";
import CheckIcon from "@mui/icons-material/Check";
import MessageIcon from "@mui/icons-material/Message";
import FlagIcon from "@mui/icons-material/Flag";
import EventIcon from "@mui/icons-material/Event";
import Tooltip from "@mui/material/Tooltip";
import InputAdornment from "@mui/material/InputAdornment";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import { Download, Search } from "@mui/icons-material";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import { useAuth } from "../hooks/AuthProvider";
import useLogExport from "../hooks/useLogExport";
import TableSearchInput from "./TableSearchInput";
import TableFilterButton from "./TableFilterButton";
import { FilterModal, FilterModalItem } from "./FilterModal";
import { CalendarIcon } from "@mui/x-date-pickers";
import ExportButton from "./ExportButton";
import ButtonTwo from "../common/dashboard-button";
import ButtonOne from "../common/normal-button";
import { ColorsConstants, searchBarWidth } from "../cssUtils/constants";
import { ReactComponent as DropdownIcon } from "../../src/assets/images/icons/logs/dropdown-arrow.svg";
import AppTable from "../common/app-table";
import AppDropdown from "../common/app-dropdown";
import {
  resolveChatLogCombinedSearchQueryForApi,
  normalizeCombinedSearchInput,
} from "../utils/phoneSearch";
import { loadFilters, saveFilters, FILTER_NAMESPACES } from "../utils/filterStorage";
import { formatWithTzLabel } from "../utils/dateUtils";

const tableColumns = [
  {
    id: "sender",
    label: "From",
    minWidth: 160,
    sortable: true,
    render: (message) => message.sender,
  },
  {
    id: "agent",
    label: "Agent",
    minWidth: 160,
    sortable: true,
  },
  {
    id: "receiver",
    label: "To",
    minWidth: 160,
    sortable: true,
    render: (message) => message.receiver,
  },
  {
    id: "logged_at",
    label: "Log Date",
    minWidth: 180,
    sortable: true,
    render: (message) =>
      formatWithTzLabel(message.logged_at, "MMMM D, YYYY, h:mm A"),
  },
  {
    id: "status",
    label: "Status",
    minWidth: 120,
    sortable: true,
  },
  {
    id: "message_type",
    label: "Message Type",
    minWidth: 160,
    sortable: true,
  },
  {
    id: "template",
    label: "Template Name",
    minWidth: 200,
    render: (message) => message?.template?.template_name,
  },
  {
  id: "message",
  label: "Message",
  minWidth: 320,
  render: (row) => {
    const message = row?.message || "-";

    return (
      <Tooltip
        arrow
        placement="top"
        title={
          <Typography
            sx={{
              fontSize: "inherit",
              fontWeight: 400,
              lineHeight: 1.5,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {message}
          </Typography>
        }
        slotProps={{
          tooltip: {
            sx: {
              maxWidth: 400,
              p: 1.5,
              borderRadius: "8px",
            },
          },
        }}
      >
        <Typography
          noWrap
          sx={{
            maxWidth: 300,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            cursor: "pointer",
          }}
        >
          {message}
        </Typography>
      </Tooltip>
    );
  },
},
];
 

export default function UsersTable({
  refreshKey,
  dateInput,
  controller,
  setController,
  lastSynced,
  setLastSynced,
}) {
  const [rowCount, setRowCount] = useState(0);
  const [messageData, setmessageData] = useState([]);
  const [userModalState, setUserModalState] = useState({
    isOpen: false,
    selectedClient: null,
    mode: "add",
  });
  const URL = API_BASE_URL;

  const [savedFilters] = useState(() => {
    const s = loadFilters(FILTER_NAMESPACES.WHATSAPP_MESSAGE_LOGS, {
      searchQuery: '', statusFilter: '', messageTypeFilter: '',
      fromDate: null, toDate: null, senderFilter: '', receiverFilter: '',
      templateFilter: '', templateName: '',
    });
    return {
      ...s,
      fromDate: s.fromDate ? dayjs(s.fromDate) : null,
      toDate: s.toDate ? dayjs(s.toDate) : null,
    };
  });
  const [searchQuery, setSearchQuery] = useState(savedFilters.searchQuery);
  const [charCount, setCharCount] = useState(savedFilters.searchQuery.length);
  const [statusFilter, setStatusFilter] = useState(savedFilters.statusFilter);
  const [messageTypeFilter, setMessageTypeFilter] = useState(savedFilters.messageTypeFilter);
  const [fromDate, setFromDate] = useState(savedFilters.fromDate);
  const { getValidToken } = useAuth();
  const [toDate, setToDate] = useState(savedFilters.toDate);
  const [dateError, setDateError] = useState(null);
  const [senderFilter, setSenderFilter] = useState(savedFilters.senderFilter);
  const [receiverFilter, setReceiverFilter] = useState(savedFilters.receiverFilter);

  const [dateAnchorEl, setDateAnchorEl] = useState(null);
  const [appliedDateRange, setAppliedDateRange] = useState([savedFilters.fromDate, savedFilters.toDate]);

  const [messageTypeAnchorEl, setMessageTypeAnchorEl] = useState(null);
  const [statusAnchorEl, setStatusAnchorEl] = useState(null);
  const [senderAnchorEl, setSenderAnchorEl] = useState(null);
  const [receiverAnchorEl, setReceiverAnchorEl] = useState(null);

  const [filterModalOpen, setFilterModalOpen] = useState(false);

  const [exportState, setExportState] = useState("default");
  const [exportSnackbar, setExportSnackbar] = useState({ open: false, message: "", severity: "info" });
  const [sortState, setSortState] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templateSearchQuery, setTemplateSearchQuery] = useState("");
  const [templateFilter, setTemplateFilter] = useState(savedFilters.templateFilter || "");
  const [templateName, setTemplateName] = useState(savedFilters.templateName || "");

  const resolvedChatLogSearch = useMemo(
    () => resolveChatLogCombinedSearchQueryForApi(searchQuery),
    [searchQuery],
  );

  const activeFilters = useMemo(
    () => ({
      search_query: searchQuery,
      status_filter: statusFilter,
      message_type_filter: messageTypeFilter,
      from_date: fromDate ? dayjs(fromDate).format("YYYY-MM-DD") : "",
      to_date: toDate ? dayjs(toDate).format("YYYY-MM-DD") : "",
      sender_filter: senderFilter,
      receiver_filter: receiverFilter,
      template_filter: templateFilter,
    }),
    [resolvedChatLogSearch, statusFilter, messageTypeFilter, fromDate, toDate, senderFilter, receiverFilter, templateFilter],
  );

  const {
    preview: exportPreview,
    loading: exportLoading,
    error: exportError,
    triggerExport,
  } = useLogExport(activeFilters);

  const wasExportLoadingRef = useRef(false);
  useEffect(() => {
    if (exportLoading) {
      setExportState("loading");
    } else if (wasExportLoadingRef.current) {
      setExportState(exportError ? "failed" : "success");
    }
    wasExportLoadingRef.current = exportLoading;
  }, [exportLoading, exportError]);

  const showExportSnackbar = (message, severity = "info") => {
    setExportSnackbar({ open: true, message, severity });
  };

  const handleCloseExportSnackbar = () => {
    setExportSnackbar((prev) => ({ ...prev, open: false }));
  };

  const sortedData = useMemo(() => {
    if (!sortState.length || !messageData.length) return messageData;
    return [...messageData].sort((a, b) => {
      for (const { id, direction } of sortState) {
        let aVal = a[id];
        let bVal = b[id];
        if (aVal == null && bVal == null) continue;
        if (aVal == null) return direction === "asc" ? 1 : -1;
        if (bVal == null) return direction === "asc" ? -1 : 1;
        if (typeof aVal === "string") {
          aVal = aVal.toLowerCase();
          bVal = String(bVal).toLowerCase();
        }
        if (aVal < bVal) return direction === "asc" ? -1 : 1;
        if (aVal > bVal) return direction === "asc" ? 1 : -1;
      }
      return 0;
    });
  }, [messageData, sortState]);

  useEffect(() => {
    saveFilters(FILTER_NAMESPACES.WHATSAPP_MESSAGE_LOGS, {
      searchQuery,
      statusFilter,
      messageTypeFilter,
      senderFilter,
      receiverFilter,
      templateFilter,
      templateName,
      fromDate: fromDate ? dayjs(fromDate).toISOString() : null,
      toDate: toDate ? dayjs(toDate).toISOString() : null,
    });
  }, [searchQuery, statusFilter, messageTypeFilter, senderFilter, receiverFilter, templateFilter, templateName, fromDate, toDate]);

  const fetchData = async () => {
    if (resolvedChatLogSearch.blockFetch) {
      setmessageData([]);
      setRowCount(0);
      setLastSynced(null);
      return;
    }
    let searchDateURL = "";
    if (dateInput) {
      let dateString = dayjs(dateInput).format("YYYY-MM-DD");
      searchDateURL = `&search_date=${dateString}`;
    }

    const queryParams = new URLSearchParams({
      limit: controller.rowsPerPage.toString(),
      offset: ((controller.serverSidePage ?? controller.page ?? 0) * controller.rowsPerPage).toString(),
      search_query: searchQuery,
      status_filter: statusFilter,
      message_type_filter: messageTypeFilter,
      from_date: fromDate ? dayjs(fromDate).format("YYYY-MM-DD") : "",
      to_date: toDate ? dayjs(toDate).format("YYYY-MM-DD") : "",
      sender_filter: senderFilter,
      receiver_filter: receiverFilter,
      template_filter: templateFilter,
    });

    // const response = await fetch(`${WHATSAPP_MESSAGE_LOG_ENDPOINT}?${queryParams}${searchDateURL}`);
    const tenant = localStorage.getItem("tenant");
    const headers = {
      "Content-Type": "application/json",
      "X-Tenant-Id": tenant,
    };

    try {
      const response = await fetch(
        `${WHATSAPP_MESSAGE_LOG_ENDPOINT}?${queryParams}${searchDateURL}`,
        { headers },
      );
      if (!response.ok) {
        throw new Error("Failed to fetch data");
      }
      const jsonData = await response.json();
      setmessageData(jsonData.results);
      setRowCount(jsonData.count);
      setLastSynced(
        jsonData.results.length > 0
          ? formatWithTzLabel(
              jsonData.results[0].last_synced,
              "MMMM D, YYYY, h:mm:ss A",
            )
          : null,
      );
    } catch (error) {
      console.error("Error fetching data:", error.message);
    }
  };

  const handleExport = () => {
    if (resolvedChatLogSearch.blockFetch) {
      setExportState("failed");
      return;
    }

    if (!exportPreview) {
      showExportSnackbar("Still calculating export size, please try again in a moment.", "warning");
      return;
    }

    showExportSnackbar(
      exportPreview.message,
      exportPreview.export_mode === "blocked" ? "error" : "info",
    );

    if (exportPreview.export_mode === "blocked") {
      return;
    }

    triggerExport();
  };

  useEffect(() => {
    fetchData();
  }, [
    controller,
    controller.serverSidePage,
    controller.rowsPerPage,
    refreshKey,
    dateInput,
    searchQuery,
    statusFilter,
    messageTypeFilter,
    fromDate,
    toDate,
    senderFilter,
    receiverFilter,
    templateFilter,
    lastSynced,
  ]);

  useEffect(() => {
    setAppliedDateRange([fromDate, toDate]);
  }, []);

  const handleDateClick = (event) => {
    setDateAnchorEl(event.currentTarget);
  };

  const handleDateRangeApply = () => {
    setFromDate(appliedDateRange[0]);
    setToDate(appliedDateRange[1]);
    setDateAnchorEl(null);
    setController({
      page: 0,
      rowsPerPage: 20,
    });
  };

  const handleDateRangeCancel = () => {
    setAppliedDateRange([fromDate, toDate]);
    setDateAnchorEl(null);
    setController({
      page: 0,
      rowsPerPage: 20,
    });
  };

  const handleMessageTypeClick = (event) => {
    setMessageTypeAnchorEl(event.currentTarget);
  };

  const handleStatusClick = (event) => {
    setStatusAnchorEl(event.currentTarget);
  };

  const handleSenderClick = (event) => {
    setSenderAnchorEl(event.currentTarget);
  };

  const handleReceiverClick = (event) => {
    setReceiverAnchorEl(event.currentTarget);
  };

  const handleMessageTypeSelect = (type) => {
    const newValue = messageTypeFilter === type ? "" : type;
    setMessageTypeFilter(newValue);
    setMessageTypeAnchorEl(null);
    setController({
      page: 0,
      rowsPerPage: 20,
    });
  };

  const handleStatusSelect = (status) => {
    const newValue = statusFilter === status ? "" : status;
    setStatusFilter(newValue);
    setStatusAnchorEl(null);
    setController({
      page: 0,
      rowsPerPage: 20,
    });
  };

  const handleSenderSelect = (value) => {
    setSenderFilter(value);
    setSenderAnchorEl(null);
  };

  const handleReceiverSelect = (value) => {
    setReceiverFilter(value);
    setReceiverAnchorEl(null);
  };

  const fetchTemplates = async () => {
    setTemplatesLoading(true);
    try {
      const token = await getValidToken();
      const tenant = localStorage.getItem("tenant");
      const response = await fetch(`${URL}/templates/names`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          "X-Tenant-Id": tenant,
        },
      });
      if (!response.ok) throw new Error(`Failed to fetch templates: ${response.status}`);
      const data = await response.json();
      const activeTemplates = data.results.filter(
        (t) => t.active && t.status === "APPROVED",
      );
      setTemplates(activeTemplates);
    } catch (error) {
      console.error("Error fetching templates:", error);
      setTemplates([]);
    } finally {
      setTemplatesLoading(false);
    }
  };

  const handleTemplateSelect = (template) => {
    if (templateFilter === template.template_id) {
      setTemplateFilter("");
      setTemplateName("");
    } else {
      setTemplateFilter(template.template_id);
      setTemplateName(template.template_name);
    }
    setController({ page: 0, rowsPerPage: 20 });
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  const updateSearchValue = (value) => {
      const normalized = normalizeCombinedSearchInput(value);

  if (value.length > 50) {
    return;
  }

  setSearchQuery(normalized);
  setCharCount(normalized.length);

  setController({
    page: 0,
    rowsPerPage: 20,
  });
};

  const handlePageChange = (event, newPage) => {
    setController((prev) => ({
      ...prev,
      page: newPage,
      serverSidePage: newPage, // Sync server-side page with UI page
    }));
  };

  const handleMessageTypeFilterChange = (event) => {
    setMessageTypeFilter(event.target.value);
  };

  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
  };

  const handleSenderFilterChange = (event) => {
    setSenderFilter(event.target.value);
  };

  const handleReceiverFilterChange = (event) => {
    setReceiverFilter(event.target.value);
  };
  const handleChangeRowsPerPage = (event) => {
    setController((prev) => ({
      ...prev,
      rowsPerPage: parseInt(event.target.value, 20),
      page: 0,
      serverSidePage: 0,
    }));
  };

  const handleFromDateChange = (newValue) => {
    if (toDate && newValue && dayjs(newValue).isAfter(toDate)) {
      setDateError("From Date must be less than or equal to To Date");
    } else {
      setDateError(null);
      setFromDate(newValue);
    }
  };

  const handleToDateChange = (newValue) => {
    if (fromDate && newValue && dayjs(newValue).isBefore(fromDate)) {
      setDateError("To Date must be greater than or equal to From Date");
    } else {
      setDateError(null);
      setToDate(newValue);
    }
  };

  const handleRefresh = () => {
    setController({
      page: 0,
      rowsPerPage: 20,
    });
  };

  const handleResetButtonClick = () => {
    setSearchQuery("");
    setStatusFilter("");
    setMessageTypeFilter("");
    setFromDate(null);
    setToDate(null);
    setAppliedDateRange([null, null]);
    setSenderFilter("");
    setReceiverFilter("");
    setTemplateFilter("");
    setTemplateName("");
    setSortState([]);
    setController({
      page: 0,
      rowsPerPage: 20,
    });
  };

  const handleSortChange = (columnId) => {
    setSortState((prev) => {
      const existing = prev.find((s) => s.id === columnId);
      if (!existing) return [...prev, { id: columnId, direction: "asc" }];
      if (existing.direction === "asc") return prev.map((s) => s.id === columnId ? { ...s, direction: "desc" } : s);
      return prev.filter((s) => s.id !== columnId);
    });
  };

  const [activeButton, setActiveButton] = useState("Message");

  const handleButtonClick = (buttonName) => {
    setActiveButton(buttonName);
    console.log(`${buttonName} clicked`);
  };

  const getTotalActiveFilters = () => {
    let count = 0;
    if (statusFilter) count++;
    if (messageTypeFilter) count++;
    if (templateFilter) count++;
    if (fromDate) count++;
    if (toDate) count++;
    return count;
  };

  return (
    <Paper
      sx={{
        width: "100%",
        overflow: "hidden",
        backgroundColor: "#F7F7F7",
        border: "none",
        boxShadow: "none",
      }}
    >
      <Box
        sx={{
          display: { xs: "flex", md: "none" },
          flexDirection: "column",
          gap: 2,
          backgroundColor: "#F7F7F7",
          px: 0,
          mx: 0,
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <ButtonOne
            variant="contained"
            startIcon={<Download />}
            onClick={handleExport}
          >
            Export
          </ButtonOne>
        </Box>
        <Divider />
        <Box
          sx={{
            display: "flex",
            mb: 2,
            gap: 1,
            px: 0,
            mx: 0,
          }}
        >
          <TableSearchInput
            searchQuery={searchQuery}
            onSearchChange={(e) => updateSearchValue(e.target.value)}
            charCount={charCount}
            placeholder="Search by phone number, template, or message"
            tooltip="Search by Phone Number, Template Name, or Message"
            error={Boolean(resolvedChatLogSearch.blockFetch && resolvedChatLogSearch.phoneSearchError)}
            helperText={resolvedChatLogSearch.blockFetch ? resolvedChatLogSearch.phoneSearchError : undefined}
          />
          <TableFilterButton
            setFilterModalOpen={setFilterModalOpen}
            totalActiveFilters={getTotalActiveFilters()}
          />
        </Box>
      </Box>
      <Box
        sx={{
          display: { xs: "none", md: "flex" },
          flexDirection: "column",
          p: 0,
          m: 1,
          bgcolor: "none",
          borderRadius: 1,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
            alignItems: "center",
            overflowX: "visible",
            width: "100%",
            minWidth: 0,
            pb: 1,
            "&::-webkit-scrollbar": { height: "8px" },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "rgba(0,0,0,0.2)",
              borderRadius: "4px",
            },
          }}
        >
          {/* Search Filter */}
          <Box sx={{ width: searchBarWidth }}>
            <TableSearchInput
              searchQuery={searchQuery}
              onSearchChange={(e) => updateSearchValue(e.target.value)}
              charCount={charCount}
              placeholder="Search by phone number, template, or message"
              tooltip="Search by Phone Number, Template Name, or Message"
              // placeholder="Search"
              error={Boolean(resolvedChatLogSearch.blockFetch && resolvedChatLogSearch.phoneSearchError)}
              helperText={resolvedChatLogSearch.blockFetch ? resolvedChatLogSearch.phoneSearchError : undefined}
            />
          </Box>

          {/* Date Range Filter */}
          <AppDropdown
            endIcon={<DropdownIcon />}
            sx={{
              whiteSpace: "nowrap",
              "& .MuiChip-root": {
                maxWidth: fromDate && toDate ? "120px" : "0px",
                transition: "max-width 0.2s ease",
              },
            }}
            paperProps={{ maxWidth: "500px" }}
            menuContent={(closeMenu) => (
              <Box sx={{ p: 2 }}>
                <LocalizationProvider
                  dateAdapter={AdapterDayjs}
                  adapterLocale="en-gb"
                >
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                  >
                    <Typography variant="subtitle2">
                      Select Date Range:
                    </Typography>
                    <Box sx={{ display: "flex", gap: 2 }}>
                      <DatePicker
                        label="From"
                        value={appliedDateRange[0]}
                        onChange={(newValue) => {
                          setAppliedDateRange([newValue, appliedDateRange[1]]);
                          if (
                            appliedDateRange[1] &&
                            newValue &&
                            dayjs(newValue).isAfter(appliedDateRange[1])
                          ) {
                            setDateError(
                              "From Date must be less than or equal to To Date",
                            );
                          } else {
                            setDateError(null);
                          }
                        }}
                        slotProps={{
                          textField: {
                            size: "small",
                            sx: {
                              width: "200px",
                              "& .MuiInputBase-root": { height: "40px" },
                            },
                          },
                        }}
                      />
                      <DatePicker
                        label="To"
                        value={appliedDateRange[1]}
                        onChange={(newValue) => {
                          setAppliedDateRange([appliedDateRange[0], newValue]);
                          if (
                            appliedDateRange[0] &&
                            newValue &&
                            dayjs(newValue).isBefore(appliedDateRange[0])
                          ) {
                            setDateError(
                              "To Date must be greater than or equal to From Date",
                            );
                          } else {
                            setDateError(null);
                          }
                        }}
                        slotProps={{
                          textField: {
                            size: "small",
                            sx: {
                              width: "200px",
                              "& .MuiInputBase-root": { height: "40px" },
                            },
                          },
                        }}
                      />
                    </Box>
                    {dateError && (
                      <Typography variant="caption" color="error">
                        {dateError}
                      </Typography>
                    )}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: 1,
                      }}
                    >
                      <ButtonTwo
                        onClick={() => {
                          handleDateRangeCancel();
                          closeMenu();
                        }}
                        variant="outlined"
                        title="CANCEL"
                      />
                      <ButtonOne
                        title="Apply"
                        onClick={() => {
                          handleDateRangeApply();
                          closeMenu();
                        }}
                        variant="contained"
                        disabled={
                          !appliedDateRange[0] ||
                          !appliedDateRange[1] ||
                          Boolean(dateError)
                        }
                      />
                    </Box>
                  </Box>
                </LocalizationProvider>
              </Box>
            )}
          >
            Log Date
            {fromDate && toDate && (
              <Tooltip
                title={`${dayjs(fromDate).format("MM/DD/YYYY")} - ${dayjs(toDate).format("MM/DD/YYYY")}`}
                placement="top"
                arrow
              >
                <Chip
                  label={`${dayjs(fromDate).format("MM/DD/YYYY")} - ${dayjs(toDate).format("MM/DD/YYYY")}`}
                  size="small"
                  onDelete={() => {
                    setFromDate(null);
                    setToDate(null);
                  }}
                  sx={{
                    ml: 1,
                    maxWidth: { sm: "80px", md: "200px" },
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                />
              </Tooltip>
            )}
          </AppDropdown>

          {/* Status Filter */}
          <AppDropdown
            endIcon={<DropdownIcon />}
            menuItems={[
              { label: "Read", value: "read" },
              { label: "Sent", value: "sent" },
              { label: "Delivered", value: "delivered" },
              { label: "Enqueued", value: "enqueued" },
              { label: "Failed", value: "failed" },
              
            ]}
            selectedValue={statusFilter}
            onMenuSelect={(value) => handleStatusSelect(value)}
          >
            Status
            {statusFilter && (
              <Tooltip title={statusFilter} placement="top" arrow>
                <Chip
                  label={statusFilter}
                  size="small"
                  onDelete={() => handleStatusSelect(statusFilter)}
                  sx={{
                    ml: 1,
                    maxWidth: "120px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                />
              </Tooltip>
            )}
          </AppDropdown>
          
          {/* Message Type Filter */}
          <AppDropdown
            endIcon={<DropdownIcon />}
            menuItems={[
              { label: "Text", value: "text" },
              { label: "Template", value: "template" },
              { label: "Interactive", value: "interactive" },
              { label: "Flow", value: "flow" },
              { label: "Button Reply", value: "button_reply" },
              { label: "List Reply", value: "list_reply" },
              { label: "Quick Reply", value: "quick_reply" },
              { label: "Button", value: "button" },
              { label: "Location", value: "location" },
              { label: "Image", value: "image" },
              { label: "Video", value: "video" },
              { label: "Audio", value: "audio" },
              { label: "Document", value: "document" },
              { label: "Sticker", value: "sticker" },
              { label: "Contacts", value: "contacts" },
              { label: "Order", value: "order" },
              { label: "Catalog", value: "catalog" },
              { label: "Multi Product", value: "multi_product" },
              { label: "File", value: "file" },
              { label: "Status Only", value: "status_only" },
            ]}
            selectedValue={messageTypeFilter}
            onMenuSelect={(value) => handleMessageTypeSelect(value)}
          >
            Message Type
            {messageTypeFilter && (
              <Tooltip title={messageTypeFilter} placement="top" arrow>
                <Chip
                  label={messageTypeFilter}
                  size="small"
                  onDelete={() => handleMessageTypeSelect(messageTypeFilter)}
                  sx={{
                    ml: 1,
                    maxWidth: "120px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                />
              </Tooltip>
            )}
          </AppDropdown>

          {/* Template Filter */}
          <AppDropdown
            endIcon={<DropdownIcon />}
            selectedValue={templateFilter}
            loading={templatesLoading}
            emptyText="No templates available"
            paperProps={{ maxWidth: "400px", maxHeight: "400px" }}
            menuContent={(closeMenu) => {
              const filteredTemplates = templates.filter((t) =>
                t.template_name.toLowerCase().includes(templateSearchQuery.toLowerCase())
              );
              return (
                <Box sx={{ p: 1 }}>
                  {templates.length > 0 && (
                    <TextField
                      placeholder="Search templates..."
                      value={templateSearchQuery}
                      onChange={(e) => setTemplateSearchQuery(e.target.value)}
                      size="small"
                      sx={{ mb: 1, width: "100%", "& .MuiInputBase-root": { height: "32px" } }}
                    />
                  )}
                  <Box sx={{ maxHeight: "320px", overflow: "auto" }}>
                    {filteredTemplates.length > 0 ? (
                      filteredTemplates.map((template) => (
                        <MenuItem
                          key={template.template_id}
                          onClick={() => {
                            handleTemplateSelect(template);
                            setTemplateSearchQuery("");
                            closeMenu();
                          }}
                          sx={{ whiteSpace: "normal", wordWrap: "break-word" }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
                            <Typography variant="body2" sx={{ flexGrow: 1 }}>
                              {template.template_name}
                            </Typography>
                            {templateFilter === template.template_id && (
                              <CheckIcon sx={{ ml: 1 }} color="success" />
                            )}
                          </Box>
                        </MenuItem>
                      ))
                    ) : (
                      <Box sx={{ p: 2, textAlign: "center", color: "gray" }}>
                        No templates found
                      </Box>
                    )}
                  </Box>
                </Box>
              );
            }}
          >
            Template Name
            {templateName && (
              <Chip
                label={templateName}
                size="small"
                onDelete={() => handleTemplateSelect({ template_id: templateFilter, template_name: templateName })}
                sx={{
                  ml: 1,
                  fontSize: "12px",
                  maxWidth: { sm: "80px", md: "150px" },
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              />
            )}
          </AppDropdown>

          {/* Reset Filter Button */}
          <ButtonTwo
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleResetButtonClick}
            sx={{
              height: "40px",
              borderRadius: "8px",
              textTransform: "capitalize",
              border: `1px solid ${ColorsConstants.primaryBackgroundColor}`,
              color: ColorsConstants.primaryBackgroundColor,
            }}
          >
            Reset
          </ButtonTwo>

          {/* Export Button */}
          <Box>
            <ExportButton
              handleExport={handleExport}
              text="Export"
              state={exportState}
              setState={setExportState}
            />
          </Box>
        </Box>
      </Box>

      {/* Table Section */}

      <AppTable
        columns={tableColumns}
        rows={sortedData}
        rowKey={(message, index) => index}
        emptyMessage="No records found"
        hideMainScroll={true}
        containerSx={{
          scrollbarWidth: "thin",
          scrollbarColor: "#C5CEDB #F3F6FB",
          "&::-webkit-scrollbar": { width: "8px" },
          "&::-webkit-scrollbar-track": { backgroundColor: "#F3F6FB", borderRadius: "8px" },
          "&::-webkit-scrollbar-thumb": { backgroundColor: "#C5CEDB", borderRadius: "8px" },
          "&::-webkit-scrollbar-thumb:hover": { backgroundColor: "#A8B4C7" },
        }}
        count={rowCount}
        page={controller.page}
        rowsPerPage={controller.rowsPerPage}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleChangeRowsPerPage}
        sortState={sortState}
        onSortChange={handleSortChange}
      />
      <FilterModal
        open={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        onReset={handleResetButtonClick}
      >
        <FilterModalItem
          name="Message Type"
          data={[
            "text",
            "template",
            "interactive",
            "flow",
            "button_reply",
            "list_reply",
            "quick_reply",
            "button",
            "location",
            "image",
            "video",
            "audio",
            "document",
            "sticker",
            "contacts",
            "order",
            "catalog",
            "multi_product",
            "file",
            "status_only",
          ]}
          value={messageTypeFilter}
          onChange={(e) => handleMessageTypeSelect(e.target.value)}
          icon={<MessageIcon />}
        />
        <FilterModalItem
          name="Template Name"
          data={templates.map((t) => t.template_name)}
          value={templateName}
          onChange={(e) => {
            const selectedName = e.target.value;
            const matchedTemplate = templates.find(
              (t) => t.template_name === selectedName,
            );
            if (matchedTemplate) {
              handleTemplateSelect(matchedTemplate);
            } else {
              // "None" selected - clear the filter
              setTemplateFilter("");
              setTemplateName("");
            }
          }}
        />
        <FilterModalItem
          name="Status"
          data={["read", "sent", "delivered", "enqueued", "failed"]}
          value={statusFilter}
          onChange={(e) => handleStatusSelect(e.target.value)}
          icon={<FlagIcon />}
        />
        <FilterModalItem
          name="From Date"
          type="date"
          value={appliedDateRange[0]}
          onChange={(newValue) => {
            setAppliedDateRange([newValue, appliedDateRange[1]]);
            // Check for date errors
            if (
              appliedDateRange[1] &&
              newValue &&
              dayjs(newValue).isAfter(appliedDateRange[1])
            ) {
              setDateError("From Date must be less than or equal to To Date");
            } else {
              setDateError(null);
              handleDateRangeApply();
            }
          }}
          icon={<CalendarIcon />}
        />
        <FilterModalItem
          name="To Date"
          type="date"
          value={appliedDateRange[1]}
          onChange={(newValue) => {
            setAppliedDateRange([appliedDateRange[0], newValue]);
            // Check for date errors
            if (
              appliedDateRange[0] &&
              newValue &&
              dayjs(newValue).isBefore(appliedDateRange[0])
            ) {
              setDateError(
                "To Date must be greater than or equal to From Date",
              );
            } else {
              setDateError(null);
              handleDateRangeApply();
            }
          }}
          icon={<CalendarIcon />}
        />
      </FilterModal>

      <Snackbar
        open={exportSnackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseExportSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseExportSnackbar}
          severity={exportSnackbar.severity}
          sx={{ width: "100%" }}
        >
          {exportSnackbar.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
}